import React, { useEffect, useMemo, useState } from 'react'
import { resolveFrontendApiBase } from '../utils/apiBase';
import dayjs, { Dayjs } from 'dayjs'
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Empty,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Table,
  Tooltip as AntTooltip,
  Typography,
} from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import './AdminDashboardSurfaces.css'

import adminAxios from './adminAxios'

const API_BASE = resolveFrontendApiBase()

const { Title, Text } = Typography
const { RangePicker } = DatePicker
const REVENUE_HEALTH_REQUEST_TIMEOUT_MS = 60 * 1000
const REVENUE_HEALTH_BACKFILL_TIMEOUT_MS = 20 * 60 * 1000

type RevenueHealthRangePreset = '7d' | '30d' | '90d' | 'mtd' | 'custom'

type RevenueHealthRange = {
  start_date: string
  end_date: string
  timezone: string
}

type RevenueHealthTimeseriesPoint = {
  date: string
  value: number
}

type RevenueHealthPlanCount = {
  plan_id: string
  plan_name: string
  count: number
  monthly_equivalent_mrr: number
  source_plan_ids?: string[]
  is_legacy?: boolean
  is_on_break?: boolean
}

type RevenueHealthCancellationReasonCount = {
  reason_code: string
  count: number
  latest_occurred_at: string | null
  sample_text: string | null
}

type RevenueHealthCohortRow = {
  cohort: string
  signups: number
  paid_conversions: number
  activated_users: number
  paid_retained_30d: number | null
}

type RevenueHealthResponse = {
  range: RevenueHealthRange
  kpis: {
    signups: number
    activated_users: number
    activation_rate: number
    paid_conversions: number
    signup_to_paid_conversion_rate: number
    trial_to_paid_conversion_rate: number | null
    active_paid_customers: number
    current_mrr: number
    new_mrr: number
    churned_mrr: number
    expansion_mrr: number
    contraction_mrr: number
    net_mrr_change: number
    logo_churn_rate: number
    revenue_churn_rate: number
    refund_count: number
    refund_amount: number
    metric_freshness: {
      rollup_generated_at: string | null
      backfill_completed_at: string | null
      partial: boolean
    }
  }
  funnel: {
    checkout_started: number | null
    checkout_sessions_created: number | null
    paid_conversions: number
    checkout_success_provisioned: number | null
    activated_users: number
  }
  timeseries: {
    daily_signups: RevenueHealthTimeseriesPoint[]
    daily_paid_conversions: RevenueHealthTimeseriesPoint[]
    daily_net_mrr_change: RevenueHealthTimeseriesPoint[]
    daily_cancellations: RevenueHealthTimeseriesPoint[]
    daily_refunds_amount: RevenueHealthTimeseriesPoint[]
    daily_contraction_mrr: RevenueHealthTimeseriesPoint[]
  }
  subscriptions: {
    active_by_plan: RevenueHealthPlanCount[]
    active_on_break_customers?: number
    canceled_in_range: number
    on_break_in_range?: number
    paused_in_range: number
    reactivated_in_range: number
    returned_from_on_break_in_range?: number
    cancellation_reasons: RevenueHealthCancellationReasonCount[]
  }
  cohorts: RevenueHealthCohortRow[]
  warning_signals: {
    failed_checkouts: number | null
    provisioning_failures: number | null
    multiple_customer_conflicts: number
    reconciliation_anomalies: number | null
    stale_billing_sync: boolean | null
    missing_plan_mapping_count: number
  }
  details: {
    refunds: Array<{
      event_id: string
      event_type: string
      occurred_at: string
      user_id: string | null
      customer_id: string | null
      invoice_id: string | null
      refund_amount: number
      source: string | null
    }>
    plan_changes: Array<{
      event_id: string
      event_type: string
      occurred_at: string
      user_id: string | null
      subscription_id: string | null
      old_plan_id: string | null
      old_plan_name: string | null
      new_plan_id: string | null
      new_plan_name: string | null
      old_monthly_mrr: number
      new_monthly_mrr: number
      source: string | null
    }>
    churn_events: Array<{
      event_id: string
      event_type: string
      occurred_at: string
      user_id: string | null
      subscription_id: string | null
      old_plan_id: string | null
      old_plan_name: string | null
      old_monthly_mrr: number
      new_monthly_mrr: number
      cancel_reason_code: string | null
      cancel_reason_text: string | null
      cancellation_reason: string | null
      cancellation_feedback: string | null
      cancellation_comment: string | null
      source: string | null
    }>
    cancellation_feedback: Array<{
      event_id: string
      occurred_at: string
      user_id: string | null
      customer_id: string | null
      subscription_id: string | null
      plan_name: string | null
      cancel_reason_code: string | null
      cancel_reason_text: string | null
      source: string | null
    }>
    retention_feedback: Array<{
      event_id: string
      occurred_at: string
      user_id: string | null
      customer_id: string | null
      subscription_id: string | null
      plan_name: string | null
      cancel_reason_code: string | null
      cancel_reason_text: string | null
      retention_offer: string | null
      source: string | null
    }>
    reconciliation_anomalies: Array<{
      window_start: string
      window_end: string
      affected_invoices: number
      generated_at: string | null
      preview: boolean
    }>
  }
  data_quality: {
    partial_metrics: boolean
    items: Array<{
      key: string
      category: 'unavailable' | 'structural_limit' | 'quality_caveat' | 'freshness_info'
      affects_partial: boolean
      message: string
    }>
  }
  metric_notes: Record<string, string>
}

type RevenueHealthTabProps = {
  isAdmin: boolean
}

type RevenueHealthAdminJob = {
  job_id: string
  job_type: string
  status: string
  trigger_source?: string | null
  created_by?: string | null
  created_at?: string | null
  started_at?: string | null
  finished_at?: string | null
  request?: Record<string, any> | null
  counts?: Record<string, any> | null
  errors_preview?: string[] | null
  result?: Record<string, any> | null
  schedule_key?: string | null
}

type RevenueHealthScheduleStatus = {
  scheduleKey: string
  label: string
  cadenceLabel: string
  timezone: string
  windowDays: number
  nextRunAt?: string | null
  lastEnqueuedAt?: string | null
  lastRunAt?: string | null
  lastStatus?: string | null
  lastJobId?: string | null
  lastResult?: Record<string, any> | null
}

type RevenueHealthJobsResponse = {
  items: RevenueHealthAdminJob[]
}

type RevenueHealthSchedulesResponse = {
  items: RevenueHealthScheduleStatus[]
}

const percent = (value: number | null | undefined): string => `${((value || 0) * 100).toFixed(1)}%`

const usd = (value: number | null | undefined): string => {
  const normalized = Number(value || 0)
  return normalized.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

const displayMetric = (value: number | null | undefined, formatter?: (value: number) => string): string => {
  if (value === null || value === undefined) return '—'
  return formatter ? formatter(value) : Number(value).toLocaleString()
}

const formatCancelReasonCode = (value: string | null | undefined): string => {
  if (!value) return '—'
  if (value === 'finished_project') return 'Finished my project'
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const resolvePresetWindow = (preset: RevenueHealthRangePreset, customRange: [Dayjs, Dayjs] | null) => {
  const today = dayjs()
  if (preset === 'custom' && customRange) {
    return {
      start: customRange[0].format('YYYY-MM-DD'),
      end: customRange[1].format('YYYY-MM-DD'),
    }
  }
  if (preset === '7d') {
    return { start: today.subtract(6, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
  }
  if (preset === '90d') {
    return { start: today.subtract(89, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
  }
  if (preset === 'mtd') {
    return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
  }
  return { start: today.subtract(29, 'day').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') }
}

const resolveBackfillWindow = (backfillStartDate: Dayjs | null) => {
  const today = dayjs().startOf('day')
  const earliestStart = today.subtract(729, 'day')
  const requestedStart = backfillStartDate?.isValid() ? backfillStartDate.startOf('day') : today.subtract(179, 'day')
  const normalizedStart = requestedStart.isBefore(earliestStart)
    ? earliestStart
    : requestedStart.isAfter(today)
      ? today
      : requestedStart
  return {
    start: normalizedStart.format('YYYY-MM-DD'),
    end: today.format('YYYY-MM-DD'),
    days: today.diff(normalizedStart, 'day') + 1,
  }
}

const Note: React.FC<{ text?: string }> = ({ text }) => {
  if (!text) return null
  return <Text type="secondary">{text}</Text>
}

const METRIC_HELP: Record<string, string> = {
  signups:
    'Users created during the selected date window. This is the signup cohort used for activation and signup-to-paid conversion.',
  activation_rate:
    'Share of users who signed up in the selected window and reached a meaningful first product-use signal by the end of the window.',
  signup_to_paid:
    'Share of users who signed up in the selected window and reached their first renewable paid conversion by the end of the window.',
  active_paid_customers:
    'Unique customers with one or more active renewable paid subscriptions as of the selected end date, excluding paused spans.',
  net_mrr_change:
    'Net monthly recurring revenue movement in the selected window: new MRR plus expansion MRR minus contraction MRR minus churned MRR.',
  stale_billing_sync:
    'Whether stored billing rollups or Stripe-derived sync state look stale for the selected window.',
  missing_plan_mappings:
    'Count of lifecycle or subscription rows that could not be mapped cleanly to a normalized plan definition.',
  partial_metrics:
    'Whether materially unavailable or incomplete metrics remain for the selected window. Structural limitations and informational caveats do not flip this on by themselves.',
  checkout_started:
    'Checkout starts for the selected window, using explicit pricing-intent events when present and otherwise server-recorded checkout attempts.',
  checkout_sessions:
    'Checkout sessions successfully created with the billing provider in the selected window.',
  paid_conversions:
    'First successful renewable paid subscription conversions in the selected window. One-time forever-credit purchases are excluded.',
  provisioned:
    'Paid conversions that completed downstream provisioning successfully.',
  activated_users:
    'Signup-scoped users from the selected window who reached a meaningful first product-use signal by the end of the window.',
  current_mrr:
    'Monthly-equivalent recurring revenue active as of the selected end date. Annual plans are normalized to monthly values.',
  new_mrr:
    'Monthly-equivalent recurring revenue added by new paid conversions during the selected window.',
  churned_mrr:
    'Monthly-equivalent recurring revenue lost from cancellations during the selected window.',
  expansion_mrr:
    'Positive monthly-equivalent revenue movement from provable upgrades during the selected window.',
  contraction_mrr:
    'Negative monthly-equivalent revenue movement from provable downgrades during the selected window.',
  logo_churn:
    'Customer churn rate for the selected window. Calculated as customers lost divided by customers active at the start of the window.',
  revenue_churn:
    'Revenue churn rate for the selected window. Calculated as churned MRR divided by starting MRR.',
  refund_count:
    'Number of refund or credit-note refund events recorded in the selected window.',
  refund_amount:
    'Total refunded dollars recorded in the selected window from refunds and credit notes.',
  canceled_in_range:
    'Count of subscription cancellations whose cancellation timestamps fell inside the selected window.',
  active_on_break_customers:
    'Unique customers currently on the On Break subscription plan as of the selected end date.',
  on_break_in_range:
    'Unique users whose subscriptions moved onto the On Break plan during the selected window.',
  paused_in_range:
    'Unique users whose subscription pause state entered a paused span during the selected window.',
  reactivated:
    'Users who returned to a paid renewable subscription during the selected window after a prior cancellation or On Break period.',
  returned_from_on_break:
    'Unique users whose subscriptions moved from On Break back to a paid plan during the selected window.',
  active_plan_name:
    'Canonical revenue-health plan grouping for operators. Multiple Stripe price IDs can merge into one row when they represent the same plan.',
  active_plan_count:
    'Number of active subscriptions in the grouped plan row as of the selected end date. This is subscription-based, so it can exceed the unique-customer KPI.',
  active_plan_mrr:
    'Monthly-equivalent recurring revenue for the active subscriptions in the grouped plan row as of the selected end date.',
  daily_signups: 'Daily counts of new signups in the selected window.',
  daily_paid_conversions: 'Daily counts of first renewable paid conversions in the selected window.',
  daily_mrr_movement: 'Daily net MRR movement combining new, expansion, contraction, and churned MRR.',
  daily_cancellations: 'Daily subscription cancellation counts in the selected window.',
  daily_refund_amount: 'Daily refunded dollars recorded from refunds and credit notes.',
  daily_contraction_mrr: 'Daily monthly-equivalent MRR lost from provable downgrades.',
  failed_checkouts:
    'Checkout sessions created without a matching paid conversion for the same session, funnel, or user. Availability depends on instrumentation coverage.',
  provisioning_failures: 'Explicit provisioning failures recorded after checkout during the selected window.',
  multiple_customer_conflicts:
    'Users currently linked to multiple Stripe customer IDs. These usually indicate billing migration or reconciliation cleanup risk.',
  reconciliation_anomalies:
    'Affected invoices from a cached dry-run Stripe credit reconciliation preview for the selected window.',
}

const MetricTitle: React.FC<{ label: string; helpKey?: keyof typeof METRIC_HELP }> = ({ label, helpKey }) => {
  const helpText = helpKey ? METRIC_HELP[helpKey] : undefined
  if (!helpText) {
    return <>{label}</>
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{label}</span>
      <AntTooltip title={helpText}>
        <InfoCircleOutlined style={{ color: 'var(--text-secondary)' }} />
      </AntTooltip>
    </span>
  )
}

const formatIso = (value?: string | null): string => {
  if (!value) return '—'
  return dayjs(value).format('YYYY-MM-DD HH:mm')
}

const isJobActive = (job?: RevenueHealthAdminJob | null): boolean =>
  ['queued', 'running'].includes(String(job?.status || '').toLowerCase())

const downloadRevenueHealthJson = (payload: RevenueHealthResponse) => {
  const filename = `revenue-health-${payload.range.start_date}-to-${payload.range.end_date}.json`
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' })
  const objectUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(objectUrl)
}

const MetricCard: React.FC<{
  title: React.ReactNode
  value: number | null | undefined
  formatter?: (value: number) => string
  note?: string
}> = ({ title, value, formatter, note }) => (
  <Card size="small" className="admin-revenue-metric-row">
    <Statistic title={title} value={displayMetric(value, formatter)} />
    <Note text={note} />
  </Card>
)

const SimpleChart: React.FC<{
  title: React.ReactNode
  data: RevenueHealthTimeseriesPoint[]
  formatter?: (value: number) => string
}> = ({ title, data, formatter }) => (
  <Card size="small" title={title} className="admin-revenue-section-card">
    {data.length ? (
      <div className="admin-revenue-chart">
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <RechartsTooltip formatter={(value: number) => (formatter ? formatter(value) : value.toLocaleString())} />
            <Line type="monotone" dataKey="value" stroke="#1677ff" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <Empty description="No data in range." image={Empty.PRESENTED_IMAGE_SIMPLE} />
    )}
  </Card>
)

const CancellationReasonChart: React.FC<{
  data: RevenueHealthCancellationReasonCount[]
}> = ({ data }) => {
  const chartData = data.map((row) => ({
    ...row,
    label: formatCancelReasonCode(row.reason_code),
  }))
  return (
    <Card size="small" title="Cancellation Reasons">
      {chartData.length ? (
        <div className="admin-revenue-chart" style={{ height: Math.max(220, chartData.length * 48) }}>
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 96 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="label" width={112} />
              <RechartsTooltip formatter={(value: number) => value.toLocaleString()} />
              <Bar dataKey="count" fill="#1677ff" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty description="No cancellation reasons in range." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Card>
  )
}

const RevenueHealthTab: React.FC<RevenueHealthTabProps> = ({ isAdmin }) => {
  const [preset, setPreset] = useState<RevenueHealthRangePreset>('30d')
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(29, 'day'),
    dayjs(),
  ])
  const [backfillStartDate, setBackfillStartDate] = useState<Dayjs | null>(dayjs().subtract(179, 'day'))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<RevenueHealthResponse | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionSuccess, setActionSuccess] = useState<string | null>(null)
  const [backfillLoading, setBackfillLoading] = useState(false)
  const [refreshLoading, setRefreshLoading] = useState(false)
  const [jobs, setJobs] = useState<RevenueHealthAdminJob[]>([])
  const [schedules, setSchedules] = useState<RevenueHealthScheduleStatus[]>([])

  const resolvedWindow = useMemo(() => resolvePresetWindow(preset, customRange), [preset, customRange])
  const backfillWindow = useMemo(() => resolveBackfillWindow(backfillStartDate), [backfillStartDate])
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York'

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false

    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const [response, jobsResponse, schedulesResponse] = await Promise.all([
          adminAxios.get<RevenueHealthResponse>(`${API_BASE}/admin/revenue-health`, {
            params: {
              start_date: resolvedWindow.start,
              end_date: resolvedWindow.end,
              timezone: browserTimezone,
            },
            timeout: REVENUE_HEALTH_REQUEST_TIMEOUT_MS,
          }),
          adminAxios.get<RevenueHealthJobsResponse>(`${API_BASE}/admin/revenue-health/jobs`, {
            params: { limit: 25 },
          }),
          adminAxios.get<RevenueHealthSchedulesResponse>(`${API_BASE}/admin/revenue-health/schedules`),
        ])
        if (!cancelled) {
          setData(response.data)
          setJobs(jobsResponse.data?.items || [])
          setSchedules(schedulesResponse.data?.items || [])
        }
      } catch (fetchError: any) {
        if (!cancelled) {
          setError(fetchError?.response?.data?.detail || 'Failed to load revenue health metrics.')
          if (fetchError?.code === 'ECONNABORTED') {
            setError('Revenue health request timed out. Use Refresh Rollups or Run Backfill, then try again.')
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => {
      cancelled = true
    }
  }, [browserTimezone, isAdmin, resolvedWindow.end, resolvedWindow.start])

  useEffect(() => {
    if (!isAdmin) return
    if (!jobs.some((job) => isJobActive(job))) return
    const interval = window.setInterval(() => {
      void reloadRevenueHealth(false)
    }, 3000)
    return () => window.clearInterval(interval)
  }, [browserTimezone, isAdmin, jobs, resolvedWindow.end, resolvedWindow.start])

  if (!isAdmin) {
    return <Alert type="warning" message="Admin access is required." />
  }

  const metricNotes = data?.metric_notes || {}
  const metricFreshness = data?.kpis.metric_freshness
  const dataQualityItems = (data?.data_quality?.items || []).filter((item) => item.key !== 'trial_to_paid_conversion_rate')
  const dataQualityGroups = {
    unavailable: dataQualityItems.filter((item) => item.category === 'unavailable'),
    structural_limit: dataQualityItems.filter((item) => item.category === 'structural_limit'),
    quality_caveat: dataQualityItems.filter((item) => item.category === 'quality_caveat'),
    freshness_info: dataQualityItems.filter((item) => item.category === 'freshness_info'),
  }
  const groupedPlanRows = (data?.subscriptions.active_by_plan || []).filter(
    (row) => (row.source_plan_ids || []).length > 1
  )
  const cancellationReasonRows = data?.subscriptions.cancellation_reasons || []
  const reloadRevenueHealth = async (showSpinner: boolean = true) => {
    if (showSpinner) {
      setLoading(true)
    }
    setError(null)
    try {
      const [response, jobsResponse, schedulesResponse] = await Promise.all([
        adminAxios.get<RevenueHealthResponse>(`${API_BASE}/admin/revenue-health`, {
          params: {
            start_date: resolvedWindow.start,
            end_date: resolvedWindow.end,
            timezone: browserTimezone,
          },
          timeout: REVENUE_HEALTH_REQUEST_TIMEOUT_MS,
        }),
        adminAxios.get<RevenueHealthJobsResponse>(`${API_BASE}/admin/revenue-health/jobs`, {
          params: { limit: 25 },
        }),
        adminAxios.get<RevenueHealthSchedulesResponse>(`${API_BASE}/admin/revenue-health/schedules`),
      ])
      setData(response.data)
      setJobs(jobsResponse.data?.items || [])
      setSchedules(schedulesResponse.data?.items || [])
    } catch (fetchError: any) {
      setError(
        fetchError?.code === 'ECONNABORTED'
          ? 'Revenue health request timed out. Use Refresh Rollups or Run Backfill, then try again.'
          : fetchError?.response?.data?.detail || 'Failed to load revenue health metrics.'
      )
    } finally {
      if (showSpinner) {
        setLoading(false)
      }
    }
  }
  const runBackfill = async () => {
    if (
      !window.confirm(
        `Run the revenue health backfill for ${backfillWindow.start} through ${backfillWindow.end} (${backfillWindow.days} days) and refresh rollups afterward?`
      )
    ) {
      return
    }
    setActionError(null)
    setActionSuccess(null)
    setBackfillLoading(true)
    try {
      await adminAxios.post(`${API_BASE}/admin/revenue-health/backfill`, {
        days: backfillWindow.days,
        timezone: browserTimezone,
        refreshRollups: true,
      }, {
        timeout: REVENUE_HEALTH_BACKFILL_TIMEOUT_MS,
      })
      setActionSuccess('Revenue health backfill completed. Dashboard metrics were refreshed.')
      await reloadRevenueHealth()
    } catch (requestError: any) {
      setActionError(
        requestError?.code === 'ECONNABORTED'
          ? 'Revenue health backfill timed out in the browser. The server may still be processing it; wait a bit, then reload or refresh rollups.'
          : requestError?.response?.data?.detail || 'Failed to run revenue health backfill.'
      )
    } finally {
      setBackfillLoading(false)
    }
  }
  const refreshRollups = async () => {
    setActionError(null)
    setActionSuccess(null)
    setRefreshLoading(true)
    try {
      const response = await adminAxios.post(`${API_BASE}/admin/revenue-health/jobs/refresh`, {
        start_date: resolvedWindow.start,
        end_date: resolvedWindow.end,
        timezone: browserTimezone,
        warm_reconciliation: true,
      })
      setActionSuccess(`Revenue health rollup refresh queued: ${response.data?.job_id || 'job created'}.`)
      await reloadRevenueHealth(false)
    } catch (requestError: any) {
      setActionError(
        requestError?.code === 'ECONNABORTED'
          ? 'Revenue health rollup enqueue timed out in the browser. Retry if the job does not appear.'
          : requestError?.response?.data?.detail || 'Failed to queue revenue health rollup refresh.'
      )
    } finally {
      setRefreshLoading(false)
    }
  }
  const jobsColumns = [
    { title: 'Job', dataIndex: 'job_type', key: 'job_type' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Trigger', key: 'trigger_source', render: (_: any, row: RevenueHealthAdminJob) => row.trigger_source || 'manual_ui' },
    {
      title: 'Window',
      key: 'window',
      render: (_: any, row: RevenueHealthAdminJob) => {
        const request = row.request || {}
        if (!request.start_date || !request.end_date) return 'custom'
        return `${request.start_date} → ${request.end_date}`
      },
    },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (value: string | null | undefined) => formatIso(value) },
    { title: 'Finished', dataIndex: 'finished_at', key: 'finished_at', render: (value: string | null | undefined) => formatIso(value) },
  ]
  const schedulesColumns = [
    { title: 'Schedule', dataIndex: 'label', key: 'label' },
    { title: 'Cadence', dataIndex: 'cadenceLabel', key: 'cadenceLabel' },
    { title: 'Next Run', dataIndex: 'nextRunAt', key: 'nextRunAt', render: (value: string | null | undefined) => formatIso(value) },
    { title: 'Last Run', dataIndex: 'lastRunAt', key: 'lastRunAt', render: (value: string | null | undefined) => formatIso(value) },
    { title: 'Last Status', dataIndex: 'lastStatus', key: 'lastStatus', render: (value: string | null | undefined) => value || 'idle' },
    { title: 'Last Job', dataIndex: 'lastJobId', key: 'lastJobId', render: (value: string | null | undefined) => value || '—' },
  ]
  const exportJson = () => {
    if (!data) {
      setActionError('Load revenue health data before exporting JSON.')
      setActionSuccess(null)
      return
    }
    setActionError(null)
    setActionSuccess(null)
    try {
      downloadRevenueHealthJson(data)
      setActionSuccess('Revenue health JSON downloaded.')
    } catch (exportError: any) {
      setActionError(exportError?.message || 'Failed to export revenue health JSON.')
    }
  }
  const activeByPlanColumns = [
    {
      title: <MetricTitle label="Plan" helpKey="active_plan_name" />,
      dataIndex: 'plan_name',
      key: 'plan_name',
      sorter: (left: RevenueHealthPlanCount, right: RevenueHealthPlanCount) =>
        left.plan_name.localeCompare(right.plan_name),
      render: (value: string, row: RevenueHealthPlanCount) => (
        <div>
          <div>
            {value}
            {row.is_on_break ? <Tag color="gold" style={{ marginLeft: 8 }}>On Break</Tag> : null}
          </div>
          {row.is_legacy ? (
            <Text type="secondary">Legacy Stripe price</Text>
          ) : null}
          {(row.source_plan_ids || []).length > 1 ? (
            <AntTooltip
              title={`Merged Stripe prices: ${(row.source_plan_ids || []).join(', ')}`}
            >
              <Text type="secondary">Merged {(row.source_plan_ids || []).length} Stripe prices</Text>
            </AntTooltip>
          ) : null}
        </div>
      ),
    },
    {
      title: <MetricTitle label="Count" helpKey="active_plan_count" />,
      dataIndex: 'count',
      key: 'count',
      sorter: (left: RevenueHealthPlanCount, right: RevenueHealthPlanCount) => left.count - right.count,
    },
    {
      title: <MetricTitle label="Monthly Eq. MRR" helpKey="active_plan_mrr" />,
      dataIndex: 'monthly_equivalent_mrr',
      key: 'monthly_equivalent_mrr',
      sorter: (left: RevenueHealthPlanCount, right: RevenueHealthPlanCount) =>
        left.monthly_equivalent_mrr - right.monthly_equivalent_mrr,
      render: (value: number) => usd(value),
    },
  ]
  const cancellationReasonColumns = [
    {
      title: 'Reason',
      dataIndex: 'reason_code',
      key: 'reason_code',
      render: (value: string) => formatCancelReasonCode(value),
      sorter: (left: RevenueHealthCancellationReasonCount, right: RevenueHealthCancellationReasonCount) =>
        formatCancelReasonCode(left.reason_code).localeCompare(formatCancelReasonCode(right.reason_code)),
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      sorter: (left: RevenueHealthCancellationReasonCount, right: RevenueHealthCancellationReasonCount) =>
        left.count - right.count,
    },
    {
      title: 'Latest',
      dataIndex: 'latest_occurred_at',
      key: 'latest_occurred_at',
      render: (value: string | null) => formatIso(value),
    },
    {
      title: 'Sample Detail',
      dataIndex: 'sample_text',
      key: 'sample_text',
      render: (value: string | null) => value || '—',
    },
  ]
  const cohortColumns = [
    { title: 'Signup Cohort', dataIndex: 'cohort', key: 'cohort' },
    { title: 'Signups', dataIndex: 'signups', key: 'signups' },
    { title: 'Paid Conversions', dataIndex: 'paid_conversions', key: 'paid_conversions' },
    { title: 'Activated Users', dataIndex: 'activated_users', key: 'activated_users' },
    {
      title: '30d Paid Retained',
      dataIndex: 'paid_retained_30d',
      key: 'paid_retained_30d',
      render: (value: number | null) => displayMetric(value),
    },
  ]
  const refundColumns = [
    { title: 'When', dataIndex: 'occurred_at', key: 'occurred_at', render: (value: string) => formatIso(value) },
    { title: 'User', dataIndex: 'user_id', key: 'user_id', render: (value: string | null) => value || '—' },
    { title: 'Invoice', dataIndex: 'invoice_id', key: 'invoice_id', render: (value: string | null) => value || '—' },
    { title: 'Amount', dataIndex: 'refund_amount', key: 'refund_amount', render: (value: number) => usd(value) },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (value: string | null) => value || '—' },
  ]
  const planChangeColumns = [
    { title: 'When', dataIndex: 'occurred_at', key: 'occurred_at', render: (value: string) => formatIso(value) },
    { title: 'Type', dataIndex: 'event_type', key: 'event_type' },
    { title: 'From', dataIndex: 'old_plan_name', key: 'old_plan_name', render: (value: string | null, row: any) => value || row.old_plan_id || '—' },
    { title: 'To', dataIndex: 'new_plan_name', key: 'new_plan_name', render: (value: string | null, row: any) => value || row.new_plan_id || '—' },
    { title: 'Old MRR', dataIndex: 'old_monthly_mrr', key: 'old_monthly_mrr', render: (value: number) => usd(value) },
    { title: 'New MRR', dataIndex: 'new_monthly_mrr', key: 'new_monthly_mrr', render: (value: number) => usd(value) },
  ]
  const churnColumns = [
    { title: 'When', dataIndex: 'occurred_at', key: 'occurred_at', render: (value: string) => formatIso(value) },
    { title: 'Type', dataIndex: 'event_type', key: 'event_type' },
    { title: 'Plan', dataIndex: 'old_plan_name', key: 'old_plan_name', render: (value: string | null, row: any) => value || row.old_plan_id || '—' },
    { title: 'Old MRR', dataIndex: 'old_monthly_mrr', key: 'old_monthly_mrr', render: (value: number) => usd(value) },
    {
      title: 'Cancellation Details',
      key: 'cancellation_details',
      render: (_: any, row: any) => {
        const hasStructuredFeedback = row.cancel_reason_code || row.cancel_reason_text
        const hasLegacyFeedback = row.cancellation_reason || row.cancellation_feedback || row.cancellation_comment
        if (!hasStructuredFeedback && !hasLegacyFeedback) {
          return '—'
        }
        return (
          <div>
            {row.cancel_reason_code ? <div>{formatCancelReasonCode(row.cancel_reason_code)}</div> : null}
            {row.cancel_reason_text ? <div><Text type="secondary">{row.cancel_reason_text}</Text></div> : null}
            {hasStructuredFeedback && hasLegacyFeedback ? <div><Text type="secondary">Stripe lifecycle metadata</Text></div> : null}
            {row.cancellation_reason ? <div>{row.cancellation_reason}</div> : null}
            {row.cancellation_feedback ? <Text type="secondary">{row.cancellation_feedback}</Text> : null}
            {row.cancellation_comment ? <div><Text type="secondary">{row.cancellation_comment}</Text></div> : null}
          </div>
        )
      },
    },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (value: string | null) => value || '—' },
  ]
  const retentionFeedbackColumns = [
    { title: 'When', dataIndex: 'occurred_at', key: 'occurred_at', render: (value: string) => formatIso(value) },
    { title: 'Plan', dataIndex: 'plan_name', key: 'plan_name', render: (value: string | null) => value || '—' },
    { title: 'Reason', dataIndex: 'cancel_reason_code', key: 'cancel_reason_code', render: (value: string | null) => formatCancelReasonCode(value) },
    { title: 'Details', dataIndex: 'cancel_reason_text', key: 'cancel_reason_text', render: (value: string | null) => value || '—' },
    { title: 'Offer', dataIndex: 'retention_offer', key: 'retention_offer', render: (value: string | null) => value || '—' },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (value: string | null) => value || '—' },
  ]
  const cancellationFeedbackColumns = [
    { title: 'When', dataIndex: 'occurred_at', key: 'occurred_at', render: (value: string) => formatIso(value) },
    { title: 'Plan', dataIndex: 'plan_name', key: 'plan_name', render: (value: string | null) => value || '—' },
    { title: 'Reason', dataIndex: 'cancel_reason_code', key: 'cancel_reason_code', render: (value: string | null) => formatCancelReasonCode(value) },
    { title: 'Details', dataIndex: 'cancel_reason_text', key: 'cancel_reason_text', render: (value: string | null) => value || '—' },
    { title: 'Subscription', dataIndex: 'subscription_id', key: 'subscription_id', render: (value: string | null) => value || '—' },
    { title: 'Source', dataIndex: 'source', key: 'source', render: (value: string | null) => value || '—' },
  ]
  const reconciliationColumns = [
    { title: 'Window', key: 'window', render: (_: any, row: any) => `${row.window_start} → ${row.window_end}` },
    { title: 'Affected Invoices', dataIndex: 'affected_invoices', key: 'affected_invoices' },
    { title: 'Generated', dataIndex: 'generated_at', key: 'generated_at', render: (value: string | null) => formatIso(value) },
  ]

  return (
    <Space
      className="admin-dashboard-surface"
      direction="vertical"
      size={16}
      style={{
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Revenue Health
        </Title>
        <Text type="secondary">
          Signup, activation, conversion, retention, and warning signals for the selected date window.
        </Text>
      </div>

      <Card size="small" className="admin-revenue-toolbar-card">
        <Space wrap className="admin-revenue-toolbar">
          <Select<RevenueHealthRangePreset>
            value={preset}
            onChange={(value) => setPreset(value)}
            style={{ minWidth: 180 }}
            options={[
              { label: 'Last 7 days', value: '7d' },
              { label: 'Last 30 days', value: '30d' },
              { label: 'Last 90 days', value: '90d' },
              { label: 'Month to date', value: 'mtd' },
              { label: 'Custom range', value: 'custom' },
            ]}
          />
          <RangePicker
            value={customRange}
            onChange={(value) => setCustomRange(value ? [value[0]!, value[1]!] : null)}
            disabled={preset !== 'custom'}
          />
          <Text type="secondary">
            {data?.range.start_date || resolvedWindow.start} to {data?.range.end_date || resolvedWindow.end}
          </Text>
          <DatePicker
            value={backfillStartDate}
            onChange={(value) => setBackfillStartDate(value)}
            allowClear={false}
          />
          <Text type="secondary">
            Backfill window: {backfillWindow.start} to {backfillWindow.end}
          </Text>
          <Text type="secondary">Rollups: {formatIso(metricFreshness?.rollup_generated_at)}</Text>
          <Text type="secondary">Backfill: {formatIso(metricFreshness?.backfill_completed_at)}</Text>
          <Text type="secondary">Partial: {data?.data_quality?.partial_metrics ? 'Yes' : 'No'}</Text>
          <Button type="primary" loading={backfillLoading} onClick={runBackfill}>
            Run Backfill
          </Button>
          <Button loading={refreshLoading} onClick={refreshRollups}>
            Refresh Rollups
          </Button>
          <Button onClick={exportJson} disabled={!data}>
            Export JSON
          </Button>
        </Space>
      </Card>

      {error ? <Alert type="error" message={error} /> : null}
      {actionError ? <Alert type="error" message={actionError} /> : null}
      {actionSuccess ? <Alert type="success" message={actionSuccess} /> : null}

      <Collapse
        defaultActiveKey={[]}
        items={[
          {
            key: 'async-jobs',
            label: (
              <Space size={8} wrap>
                <Text strong>Async Jobs</Text>
                <Text type="secondary">
                  {jobs.length} recent jobs · {schedules.length} schedules
                </Text>
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <Title level={5}>Schedules</Title>
                  <Table
                    rowKey={(row: RevenueHealthScheduleStatus) => row.scheduleKey}
                    dataSource={schedules}
                    columns={schedulesColumns}
                    pagination={false}
                    locale={{ emptyText: 'No schedules configured.' }}
                    scroll={{ x: 900 }}
                  />
                </div>
                <div>
                  <Title level={5}>Recent Jobs</Title>
                  <Table
                    rowKey={(row: RevenueHealthAdminJob) => row.job_id}
                    dataSource={jobs}
                    columns={jobsColumns}
                    pagination={{ pageSize: 5 }}
                    locale={{ emptyText: 'No revenue health jobs yet.' }}
                    scroll={{ x: 900 }}
                  />
                </div>
              </Space>
            ),
          },
        ]}
      />

      <Spin spinning={loading}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Row gutter={[16, 16]} className="admin-revenue-overview-band">
            <Col xs={24} md={12} xl={4}>
              <MetricCard title={<MetricTitle label="New Signups" helpKey="signups" />} value={data?.kpis.signups} />
            </Col>
            <Col xs={24} md={12} xl={4}>
              <MetricCard title={<MetricTitle label="Activation Rate" helpKey="activation_rate" />} value={data?.kpis.activation_rate} formatter={percent} />
            </Col>
            <Col xs={24} md={12} xl={4}>
              <MetricCard title={<MetricTitle label="Signup to Paid" helpKey="signup_to_paid" />} value={data?.kpis.signup_to_paid_conversion_rate} formatter={percent} />
            </Col>
            <Col xs={24} md={12} xl={4}>
              <MetricCard title={<MetricTitle label="Active Paid Customers" helpKey="active_paid_customers" />} value={data?.kpis.active_paid_customers} />
            </Col>
            <Col xs={24} md={12} xl={4}>
              <MetricCard
                title={<MetricTitle label="Net MRR Change" helpKey="net_mrr_change" />}
                value={data?.kpis.net_mrr_change}
                formatter={usd}
                note={metricNotes.expansion_mrr || metricNotes.contraction_mrr}
              />
            </Col>
          </Row>

          <Card title="Data Quality" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Statistic title={<MetricTitle label="Stale Billing Sync" helpKey="stale_billing_sync" />} value={data?.warning_signals.stale_billing_sync ? 'Yes' : 'No'} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title={<MetricTitle label="Missing Plan Mappings" helpKey="missing_plan_mappings" />} value={displayMetric(data?.warning_signals.missing_plan_mapping_count)} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title={<MetricTitle label="Partial Metrics" helpKey="partial_metrics" />} value={data?.data_quality?.partial_metrics ? 'Yes' : 'No'} />
              </Col>
            </Row>
            {dataQualityItems.length ? (
              <div>
                {dataQualityGroups.unavailable.length ? (
                  <div>
                    <Text strong>Unavailable Metrics</Text>
                    {dataQualityGroups.unavailable.map((item) => (
                      <div key={item.key}>
                        <Text strong>{item.key}: </Text>
                        <Text type="secondary">{item.message}</Text>
                      </div>
                    ))}
                  </div>
                ) : null}
                {dataQualityGroups.structural_limit.length ? (
                  <div>
                    <Text strong>Definition Limits</Text>
                    {dataQualityGroups.structural_limit.map((item) => (
                      <div key={item.key}>
                        <Text strong>{item.key}: </Text>
                        <Text type="secondary">{item.message}</Text>
                      </div>
                    ))}
                  </div>
                ) : null}
                {dataQualityGroups.quality_caveat.length ? (
                  <div>
                    <Text strong>Quality Caveats</Text>
                    {dataQualityGroups.quality_caveat.map((item) => (
                      <div key={item.key}>
                        <Text strong>{item.key}: </Text>
                        <Text type="secondary">{item.message}</Text>
                      </div>
                    ))}
                  </div>
                ) : null}
                {dataQualityGroups.freshness_info.length ? (
                  <div>
                    <Text strong>Freshness</Text>
                    {dataQualityGroups.freshness_info.map((item) => (
                      <div key={item.key}>
                        <Text strong>{item.key}: </Text>
                        <Text type="secondary">{item.message}</Text>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <Empty description="No data quality notes." image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>

          <Card title="Funnel" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8} xl={4}>
                <MetricCard
                  title={<MetricTitle label="Checkout Started" helpKey="checkout_started" />}
                  value={data?.funnel.checkout_started}
                  note={metricNotes['funnel.checkout_started']}
                />
              </Col>
              <Col xs={24} md={8} xl={4}>
                <MetricCard
                  title={<MetricTitle label="Checkout Sessions" helpKey="checkout_sessions" />}
                  value={data?.funnel.checkout_sessions_created}
                  note={metricNotes['funnel.checkout_sessions_created']}
                />
              </Col>
              <Col xs={24} md={8} xl={4}>
                <MetricCard title={<MetricTitle label="Paid Conversions" helpKey="paid_conversions" />} value={data?.funnel.paid_conversions} />
              </Col>
              <Col xs={24} md={8} xl={4}>
                <MetricCard
                  title={<MetricTitle label="Provisioned" helpKey="provisioned" />}
                  value={data?.funnel.checkout_success_provisioned}
                  note={metricNotes['funnel.checkout_success_provisioned']}
                />
              </Col>
              <Col xs={24} md={8} xl={4}>
                <MetricCard title={<MetricTitle label="Activated Users" helpKey="activated_users" />} value={data?.funnel.activated_users} />
              </Col>
            </Row>
          </Card>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Card title="Retention & Revenue" size="small">
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="MRR" helpKey="current_mrr" />} value={displayMetric(data?.kpis.current_mrr, usd)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="New MRR" helpKey="new_mrr" />} value={displayMetric(data?.kpis.new_mrr, usd)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Churned MRR" helpKey="churned_mrr" />} value={displayMetric(data?.kpis.churned_mrr, usd)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Expansion MRR" helpKey="expansion_mrr" />} value={displayMetric(data?.kpis.expansion_mrr, usd)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Contraction MRR" helpKey="contraction_mrr" />} value={displayMetric(data?.kpis.contraction_mrr, usd)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Logo Churn" helpKey="logo_churn" />} value={displayMetric(data?.kpis.logo_churn_rate, percent)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Revenue Churn" helpKey="revenue_churn" />} value={displayMetric(data?.kpis.revenue_churn_rate, percent)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Refund Count" helpKey="refund_count" />} value={displayMetric(data?.kpis.refund_count)} />
                    </Col>
                    <Col span={12}>
                      <Statistic title={<MetricTitle label="Refund Amount" helpKey="refund_amount" />} value={displayMetric(data?.kpis.refund_amount, usd)} />
                    </Col>
                  </Row>
                  <Note text={metricNotes.refund_count || metricNotes.refund_amount} />
                </Card>
                <CancellationReasonChart data={cancellationReasonRows} />
                <Card title="Cancellation Reason Rollup" size="small">
                  <Table
                    rowKey="reason_code"
                    pagination={false}
                    size="small"
                    dataSource={cancellationReasonRows}
                    columns={cancellationReasonColumns}
                    locale={{ emptyText: 'No cancellation reasons in range.' }}
                  />
                </Card>
              </Space>
            </Col>
            <Col xs={24} xl={12}>
              <Card title="Subscription Health" size="small">
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Canceled in Range" helpKey="canceled_in_range" />} value={displayMetric(data?.subscriptions.canceled_in_range)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Active On Break" helpKey="active_on_break_customers" />} value={displayMetric(data?.subscriptions.active_on_break_customers)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Moved to On Break" helpKey="on_break_in_range" />} value={displayMetric(data?.subscriptions.on_break_in_range)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Paused Users in Range" helpKey="paused_in_range" />} value={displayMetric(data?.subscriptions.paused_in_range)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Reactivated" helpKey="reactivated" />} value={displayMetric(data?.subscriptions.reactivated_in_range)} />
                  </Col>
                  <Col xs={12} md={6}>
                    <Statistic title={<MetricTitle label="Returned From On Break" helpKey="returned_from_on_break" />} value={displayMetric(data?.subscriptions.returned_from_on_break_in_range)} />
                  </Col>
                </Row>
                <Note text={metricNotes['subscriptions.paused_in_range']} />
                <Table
                  rowKey="plan_id"
                  pagination={false}
                  size="small"
                  dataSource={data?.subscriptions.active_by_plan || []}
                  columns={activeByPlanColumns}
                  locale={{ emptyText: 'No active subscriptions in range.' }}
                />
                <Note text="Plan rows count active subscriptions grouped by canonical plan and legacy/current status. Counts can exceed Active Paid Customers when one customer has multiple active subscriptions." />
                {groupedPlanRows.length ? (
                  <Note text={`${groupedPlanRows.length} active plan rows consolidate multiple Stripe price IDs inside the same legacy/current plan group.`} />
                ) : null}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily Signups" helpKey="daily_signups" />} data={data?.timeseries.daily_signups || []} />
            </Col>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily Paid Conversions" helpKey="daily_paid_conversions" />} data={data?.timeseries.daily_paid_conversions || []} />
            </Col>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily MRR Movement" helpKey="daily_mrr_movement" />} data={data?.timeseries.daily_net_mrr_change || []} formatter={usd} />
            </Col>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily Cancellations" helpKey="daily_cancellations" />} data={data?.timeseries.daily_cancellations || []} />
            </Col>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily Refund Amount" helpKey="daily_refund_amount" />} data={data?.timeseries.daily_refunds_amount || []} formatter={usd} />
            </Col>
            <Col xs={24} xl={12}>
              <SimpleChart title={<MetricTitle label="Daily Contraction MRR" helpKey="daily_contraction_mrr" />} data={data?.timeseries.daily_contraction_mrr || []} formatter={usd} />
            </Col>
          </Row>

          <Card title="Cohorts" size="small">
            <Table
              rowKey="cohort"
              pagination={false}
              size="small"
              dataSource={data?.cohorts || []}
              columns={cohortColumns}
              locale={{ emptyText: 'No cohorts in range.' }}
            />
          </Card>

          <Card title="Warning Signals" size="small">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={6}>
                <MetricCard
                  title={<MetricTitle label="Failed Checkouts" helpKey="failed_checkouts" />}
                  value={data?.warning_signals.failed_checkouts}
                  note={metricNotes['warning_signals.failed_checkouts']}
                />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <MetricCard
                  title={<MetricTitle label="Provisioning Failures" helpKey="provisioning_failures" />}
                  value={data?.warning_signals.provisioning_failures}
                  note={metricNotes['warning_signals.provisioning_failures']}
                />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <MetricCard
                  title={<MetricTitle label="Multiple Stripe Customers" helpKey="multiple_customer_conflicts" />}
                  value={data?.warning_signals.multiple_customer_conflicts}
                />
              </Col>
              <Col xs={24} md={12} xl={6}>
                <MetricCard
                  title={<MetricTitle label="Reconciliation Anomalies" helpKey="reconciliation_anomalies" />}
                  value={data?.warning_signals.reconciliation_anomalies}
                  note={metricNotes['warning_signals.reconciliation_anomalies']}
                />
              </Col>
            </Row>
          </Card>

          <Card title="Details" size="small">
            <details>
              <summary>Recent Refunds</summary>
              <Table
                rowKey="event_id"
                pagination={false}
                size="small"
                dataSource={data?.details.refunds || []}
                columns={refundColumns}
                locale={{ emptyText: 'No recent refunds.' }}
              />
            </details>
            <details>
              <summary>Recent Plan Changes</summary>
              <Table
                rowKey="event_id"
                pagination={false}
                size="small"
                dataSource={data?.details.plan_changes || []}
                columns={planChangeColumns}
                locale={{ emptyText: 'No recent plan changes.' }}
              />
            </details>
            <details>
              <summary>Recent Churn / Reactivation</summary>
              <Table
                rowKey="event_id"
                pagination={false}
                size="small"
                dataSource={data?.details.churn_events || []}
                columns={churnColumns}
                locale={{ emptyText: 'No recent churn or reactivation events.' }}
              />
            </details>
            <details>
              <summary>Recent Cancellation Reasons</summary>
              <Table
                rowKey="event_id"
                pagination={false}
                size="small"
                dataSource={data?.details.cancellation_feedback || []}
                columns={cancellationFeedbackColumns}
                locale={{ emptyText: 'No recent cancellation feedback.' }}
              />
            </details>
            <details>
              <summary>Recent Retention Saves</summary>
              <Table
                rowKey="event_id"
                pagination={false}
                size="small"
                dataSource={data?.details.retention_feedback || []}
                columns={retentionFeedbackColumns}
                locale={{ emptyText: 'No recent retained cancellation feedback.' }}
              />
            </details>
            <details>
              <summary>Recent Reconciliation Anomalies</summary>
              <Table
                rowKey="window_start"
                pagination={false}
                size="small"
                dataSource={data?.details.reconciliation_anomalies || []}
                columns={reconciliationColumns}
                locale={{ emptyText: 'No recent reconciliation anomalies.' }}
              />
            </details>
          </Card>
        </Space>
      </Spin>
    </Space>
  )
}

export default RevenueHealthTab
